import { callEdgeFunction } from '../shared/api'

const record = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const identity = value => typeof value === 'string' && value.length > 0
const instant = value => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value))
const studentLabel = value => record(value) && identity(value.id) && typeof value.name === 'string'
const bookingStates = new Set(['pending', 'confirmed', 'expired', 'rejected', 'cancelled', 'completed'])

function validBlock(value) {
  return record(value) && identity(value.id) && instant(value.start_at) && instant(value.end_at)
    && ['1:1', '1:2'].includes(value.appointment_format) && ['online', 'offline'].includes(value.meeting_mode)
    && ['details', 'meeting_link', 'location'].every(key => value[key] == null || typeof value[key] === 'string')
}

function validBooking(value) {
  return record(value) && identity(value.id) && identity(value.primary_student_id) && identity(value.block_id)
    && bookingStates.has(value.state) && Number.isInteger(value.revision) && value.revision > 0
    && (value.block == null || validBlock(value.block))
    && ['partner_name', 'other_partner_name'].every(key => value[key] == null || typeof value[key] === 'string')
}

function responseFailure(payload, status) {
  return {
    data: null,
    error: typeof payload?.error === 'string' ? payload.error : 'Office hours request failed.',
    code: typeof payload?.code === 'string' ? payload.code : 'office_hours_unavailable',
    status,
    retry: payload?.retry === true,
    unknown: payload?.mutation_committed !== false && (!status || status >= 500 || (status < 300 && payload?.ok !== false)),
  }
}

async function request(token, operation, payload) {
  try {
    const data = await callEdgeFunction('office-hours', { token, operation, ...payload }, { cache: false, timeoutMs: 20_000 })
    if (data?.ok !== true) return responseFailure(data, 200)
    return { data, error: null, code: null, unknown: false }
  } catch (err) {
    return responseFailure(err.payload || { error: err.message, code: err.code, retry: err.retry }, err.status)
  }
}

export async function fetchOfficeHours(token, studentId) {
  const result = await request(token, 'view', { student_id: studentId })
  if (result.error) return result
  const data = result.data
  if (!Array.isArray(data.blocks) || !Array.isArray(data.bookings) || !Array.isArray(data.students)
    || !record(data.partners) || !record(data.usage) || !identity(data.semester_id) || !instant(data.server_time)
    || !data.students.every(s => studentLabel(s) && typeof s.eligible === 'boolean')
    || !data.blocks.every(validBlock) || !data.bookings.every(validBooking)
    || !Object.values(data.partners).every(list => Array.isArray(list) && list.every(studentLabel))
    || !Object.values(data.usage).every(value => record(value) && typeof value.used === 'boolean' && typeof value.unresolved === 'boolean')) {
    return responseFailure({ code: 'office_hours_contract_invalid' }, 200)
  }
  return result
}

export async function mutateOfficeHours(token, operation, payload, mutationId) {
  const result = await request(token, operation, { ...payload, mutation_id: mutationId })
  if (result.error) return result
  const receipt = result.data
  const booking = receipt.data?.booking
  const targetMatches = operation === 'reserve'
    ? booking?.primary_student_id === payload.primary_student_id && booking?.block_id === payload.block_id
    : operation === 'cancel' && booking?.id === payload.booking_id
  if (receipt.operation !== (operation === 'cancel' ? 'booking_cancel' : 'reserve')
    || receipt.mutation_id !== mutationId || typeof receipt.replayed !== 'boolean'
    || !targetMatches || !validBooking(booking)) {
    return responseFailure({ code: 'office_hours_receipt_invalid' }, 200)
  }
  return result
}
